import React, { useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';

export interface UnderlineTabItem {
  value: string;
  label: React.ReactNode;
}

interface UnderlineTabsProps {
  items: UnderlineTabItem[];
  value: string;
  onChange: (value: string) => void;
  /** Rangée défilante horizontalement (catégories nombreuses sur mobile). */
  scrollable?: boolean;
  size?: 'md' | 'sm';
  className?: string;
  'aria-label'?: string;
}

/**
 * Onglets soulignés — la grammaire de sélecteur de la charte (« un sélecteur
 * est un onglet, jamais un dropdown ») : texte, et un trait dégradé sous
 * l'onglet actif. Pas de pastille, pas d'aplat.
 *
 * En `scrollable`, la rangée défile sans barre visible, avec un fondu sur le
 * bord droit qui dit qu'il y a la suite, et l'onglet choisi se recentre.
 */
export const UnderlineTabs: React.FC<UnderlineTabsProps> = ({
  items, value, onChange, scrollable = false, size = 'md', className, 'aria-label': ariaLabel,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scrollable || !listRef.current) return;
    const active = listRef.current.querySelector<HTMLElement>('[aria-selected="true"]');
    active?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [value, scrollable]);

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex items-end',
        size === 'md' ? 'gap-6 border-b border-border' : 'gap-5',
        scrollable && 'flex-nowrap overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(90deg,#000_calc(100%-28px),transparent)]',
        !scrollable && 'flex-wrap',
        className,
      )}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={cn(
              'relative shrink-0 whitespace-nowrap bg-transparent px-0.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              size === 'md' ? 'pb-3 pt-2.5 text-sm' : 'pb-2.5 pt-2 text-sm',
              active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
              'after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full after:bg-gradient-to-r after:from-red-500 after:via-orange-500 after:to-yellow-400 after:transition-opacity', // check-colors: ignore — dégradé de marque (soulignement d'onglet actif, charte §2c)
              active ? 'after:opacity-100' : 'after:opacity-0',
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
};

export default UnderlineTabs;
